import React, { MouseEventHandler, Suspense, lazy } from "react";
import classNames from "classnames";
import { twMerge } from "tailwind-merge";
import { IconSizes } from "@ugitgud/legos/ui/shared";
import type { ImageProps, StaticImageData } from "next/image";
import { iconLookup } from "@ugitgud/legos/assets/lib/icon";

export type IconName = keyof typeof iconLookup;

export type IconProps = {
  className?: string;
  icon: React.ReactSVGElement | IconName | JSX.Element | React.ReactNode;
  label?: string;
  size?: IconSizes;
  width?: number | `${number}`;
  height?: number | `${number}`;
  mono?: boolean;
  wide?: boolean;
  onClick?: MouseEventHandler<HTMLSpanElement>;
};

const isStaticImageData = (url: ImageProps["src"]): url is StaticImageData => {
  return (url as StaticImageData)?.src !== undefined;
};

// note(jsimms): since nextjs loads images with a custom loader we have to type guard this
const resolveUrl = (url: string | ImageProps["src"] | null | undefined) => {
  if (url === null || url === undefined) {
    return undefined;
  }
  if (typeof url === "string") {
    return url;
  }
  if (isStaticImageData(url)) {
    return url.src;
  }
  return url.default.src;
};

function isIconName(iconName: any): iconName is IconName {
  if (typeof iconName !== "string") {
    return false;
  }
  return !!(iconLookup as Record<string, any>)[iconName];
}

export const Icon: React.FC<IconProps> = ({ icon, mono: fill, wide, className, size = "sm", onClick, label, width, height }) => {
  const isNamedIcon = isIconName(icon);
  const iconUrl = isNamedIcon ? iconLookup[icon] : undefined;
  const url = isNamedIcon ? resolveUrl(iconUrl) : undefined;
  return (
    <span
      role="img"
      aria-label={label}
      style={
        fill ? { maskImage: isNamedIcon ? `url('${url}')` : undefined, maskSize: "contain", maskRepeat: "no-repeat" } : undefined
      }
      className={twMerge(
        classNames("flex-shrink-0 icon inline-flex items-start justify-start", `icon-${size.toString()}`, className, {
          "icon-mono": fill,
          wide,
        }),
      )}
      onClick={onClick}
    >
      {!isNamedIcon && icon}
      {isNamedIcon && !fill && url && <>{<img className="w-auto h-full" src={url} />}</>}
    </span>
  );
};
